import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  listInvoices,
  createInvoice,
  createInvoiceFromReport,
  retryInvoice,
  sendInvoice,
  refreshInvoice,
  markInvoicePaid,
  deleteInvoice,
  listBillingPresets,
  listCustomers,
  getReport,
} from '../services/api';

const SYNC_COLORS = {
  pending: '#94a3b8',
  syncing: '#0d6efd',
  synced: '#16a34a',
  error: '#d9534f',
};
// E-invoicing (PEPPOL) outcome reported by InvoiceNinja — companies only.
const E_INVOICE_COLORS = {
  sent: '#64748b',
  delivered: '#16a34a',
  failed: '#d9534f',
};
const BIZ_COLORS = {
  draft: '#92400e',
  sent: '#0d6efd',
  partial: '#e8912d',
  paid: '#16a34a',
  cancelled: '#64748b',
};

// Belgian VAT rates: 21 % standard, 6 % reduced (renovation).
const VAT_RATES = ['21', '6'];

const emptyLine = () => ({
  designation: '',
  quantity: '1',
  unit_price: '0',
  tax_rate: '21',
  tax_name: 'TVA',
  preset_id: null,
});

const todayISO = () => new Date().toISOString().slice(0, 10);
// PEPPOL buyer-reference default: <SANITISED_NAME>_<YYYY-MM-DD>.
const sanitizePo = (name) =>
  ((name || '').trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '').toUpperCase() || 'FACT');
const poDefault = (name) => `${sanitizePo(name)}_${todayISO()}`;

// Where an invoice actually goes: the dedicated billing address if the party
// has one, else its contact address. Mirrors the backend snapshot rule.
const billingEmailOf = (party) =>
  ((party && party.billing_email) || '').trim() || ((party && party.email) || '').trim();

const EMPTY_MANUAL = {
  name: '', address: '', email: '', phone: '',
  vat: '', postal_code: '', city: '', country: 'BE',
};
const MANUAL_COUNTRIES = ['BE', 'FR', 'NL', 'LU', 'DE', 'GB', 'ES', 'IT', 'PT', 'CH', 'AT', 'IE', 'US', 'CA'];

function Badge({ color, children, title }) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        color: '#fff',
        background: color || '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
      }}
    >
      {children}
    </span>
  );
}

export default function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [presets, setPresets] = useState([]);

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [reportContext, setReportContext] = useState(null); // { id, title, billToName }
  const [billTo, setBillTo] = useState(''); // "company:id" | "customer:id" | "manual"
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [currency, setCurrency] = useState('EUR');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  // Amount already settled (cash/transfer on site) at issue time.
  const [amountPaid, setAmountPaid] = useState('0');
  const [reportFilter, setReportFilter] = useState(null); // { id, title } | null
  const [lines, setLines] = useState([emptyLine()]);
  const [presetToAdd, setPresetToAdd] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const money = useCallback(
    (value) => {
      const n = Number(value || 0);
      try {
        return new Intl.NumberFormat(i18n.language || 'fr', {
          style: 'currency',
          currency: currency || 'EUR',
        }).format(n);
      } catch {
        return `${n.toFixed(2)} ${currency}`;
      }
    },
    [currency, i18n.language]
  );

  // Row-level formatter: each invoice carries its own currency, unlike `money`
  // which follows the editor form.
  const fmtMoney = useCallback((value, cur) => {
    const n = Number(value || 0);
    try {
      return new Intl.NumberFormat(i18n.language || 'fr', {
        style: 'currency',
        currency: cur || 'EUR',
      }).format(n);
    } catch {
      return `${n.toFixed(2)} ${cur || 'EUR'}`;
    }
  }, [i18n.language]);

  const fmtDate = useCallback((raw) => {
    const loc = i18n.language || 'fr';
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString(loc);
      return raw; // free-text expertise date
    }
    return new Date().toLocaleDateString(loc);
  }, [i18n.language]);
  // Default invoice note: "Expertise du <local date>".
  const defaultNote = useCallback((raw) => `${t('invoices.expertiseNotePrefix')}${fmtDate(raw)}`, [t, fmtDate]);

  const fetchInvoices = useCallback(() => {
    return listInvoices().then(setInvoices).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      fetchInvoices(),
      listCustomers().then(setCustomers).catch(() => {}),
      listBillingPresets().then(setPresets).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [fetchInvoices]);

  // "Facturer" a report (?invoice_for=<id>): open the editor prefilled.
  const invoiceForParam = searchParams.get('invoice_for');
  useEffect(() => {
    if (!invoiceForParam) return;
    getReport(invoiceForParam)
      .then((report) => {
        // Same rule as the server: the billing client (the company) is
        // the payer when there is one, the end customer otherwise.
        const party = report.requesting_company || report.customer;
        const name = party ? party.name : '';
        setReportContext({
          id: report.id,
          title: report.title,
          billToName: name,
          billToKind: report.requesting_company ? 'company' : 'customer',
          billToEmail: party ? billingEmailOf(party) : '',
        });
        setPoNumber(poDefault(name));
        setNotes(defaultNote(report.expertise_date));
        setLines([emptyLine()]);
        setShowEditor(true);
      })
      .catch(() => {})
      .finally(() => {
        searchParams.delete('invoice_for');
        setSearchParams(searchParams, { replace: true });
      });
  }, [invoiceForParam]); // eslint-disable-line

  // "Voir factures liées" (?report=<id>): filter the list to that report.
  const reportParam = searchParams.get('report');
  useEffect(() => {
    if (!reportParam) { setReportFilter(null); return; }
    getReport(reportParam)
      .then((r) => setReportFilter({ id: r.id, title: r.title }))
      .catch(() => setReportFilter({ id: reportParam, title: reportParam }));
  }, [reportParam]);

  // Poll while anything is in flight so the UI reflects the background sync.
  const pollRef = useRef();
  useEffect(() => {
    const inFlight = invoices.some((i) => i.sync_status === 'pending' || i.sync_status === 'syncing');
    clearInterval(pollRef.current);
    if (inFlight) {
      pollRef.current = setInterval(fetchInvoices, 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [invoices, fetchInvoices]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let tax = 0;
    for (const l of lines) {
      const line = Number(l.quantity || 0) * Number(l.unit_price || 0);
      subtotal += line;
      tax += (line * Number(l.tax_rate || 0)) / 100;
    }
    return { subtotal, tax, total: subtotal + tax };
  }, [lines]);

  // Who the invoice will be addressed and e-mailed to, whatever the mode.
  const recipient = useMemo(() => {
    if (reportContext) {
      return {
        name: reportContext.billToName,
        email: reportContext.billToEmail,
        kind: reportContext.billToKind,
      };
    }
    // One list of clients: whoever is picked is the billed client.
    if (billTo.startsWith('company:')) {
      const c = customers.find((x) => `company:${x.id}` === billTo);
      return c ? { name: c.name, email: billingEmailOf(c), kind: 'company' } : null;
    }
    if (billTo === 'manual' && manual.name.trim()) {
      return { name: manual.name, email: (manual.email || '').trim(), kind: 'manual' };
    }
    return null;
  }, [reportContext, billTo, customers, manual]);

  // Clamped the same way the server does: never above the total, never below 0.
  const paidNum = Math.min(Math.max(Number(amountPaid || 0), 0), totals.total);
  const dueNum = Math.max(0, totals.total - paidNum);

  const resetEditor = () => {
    setShowEditor(false);
    setReportContext(null);
    setBillTo('');
    setManual(EMPTY_MANUAL);
    setCurrency('EUR');
    setPoNumber('');
    setNotes('');
    setAmountPaid('0');
    setLines([emptyLine()]);
    setPresetToAdd('');
    setError('');
  };

  // Open a fresh standalone editor with the default "Expertise du <today>" note.
  const openNewInvoice = () => {
    resetEditor();
    setNotes(defaultNote());
    setShowEditor(true);
  };

  const addPresetLine = (presetId) => {
    const p = presets.find((x) => x.id === presetId);
    if (!p) return;
    setLines((prev) => [
      ...prev,
      {
        designation: p.label,
        quantity: '1',
        unit_price: String(p.default_price),
        tax_rate: String(p.default_tax_rate),
        tax_name: p.default_tax_name || 'TVA',
        preset_id: p.id,
      },
    ]);
    setPresetToAdd('');
  };

  const updateLine = (idx, field, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    const validLines = lines
      .filter((l) => (l.designation || '').trim() && Number(l.quantity) > 0)
      .map((l) => ({
        designation: l.designation,
        quantity: String(l.quantity),
        unit_price: String(l.unit_price || 0),
        tax_rate: String(l.tax_rate || 0),
        tax_name: l.tax_name || 'TVA',
        preset_id: l.preset_id || null,
      }));
    if (validLines.length === 0) {
      setError(t('invoices.errorNoLines'));
      return;
    }
    setSaving(true);
    try {
      if (reportContext) {
        await createInvoiceFromReport(reportContext.id, {
          currency,
          po_number: poNumber,
          notes,
          amount_paid: String(paidNum),
          line_items: validLines,
        });
      } else {
        const payload = {
          currency, po_number: poNumber, notes,
          amount_paid: String(paidNum),
          line_items: validLines,
        };
        if (billTo.startsWith('company:')) {
          payload.bill_to_kind = 'company';
          payload.requesting_company_id = billTo.split(':')[1];
        } else {
          payload.bill_to_kind = 'company';
          payload.bill_to_name = manual.name;
          payload.bill_to_address = manual.address;
          payload.bill_to_email = manual.email;
          payload.bill_to_phone = manual.phone;
          payload.bill_to_vat = manual.vat;
          payload.bill_to_postal_code = manual.postal_code;
          payload.bill_to_city = manual.city;
          payload.bill_to_country_code = manual.country;
        }
        if (!reportContext && billTo === '' && !manual.name.trim()) {
          setError(t('invoices.errorNoBillTo'));
          setSaving(false);
          return;
        }
        await createInvoice(payload);
      }
      await fetchInvoices();
      resetEditor();
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (id, fn) => {
    setBusyId(id);
    try {
      await fn(id);
      await fetchInvoices();
    } catch {
      // surfaced via the row's error badge after refresh
      await fetchInvoices();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('invoices.deleteConfirm'))) return;
    await runAction(id, deleteInvoice);
  };

  // Settle the outstanding balance: recorded locally, then notified to
  // InvoiceNinja as a payment.
  const handleMarkPaid = async (inv) => {
    const amount = fmtMoney(inv.amount_due, inv.currency);
    if (!window.confirm(t('invoices.markPaidConfirm', { amount }))) return;
    setBusyId(inv.id);
    try {
      await markInvoicePaid(inv.id);
    } catch (err) {
      window.alert(err.response?.data?.detail || t('errors.generic'));
    } finally {
      await fetchInvoices();
      setBusyId(null);
    }
  };

  // Invoices already created for the report currently being invoiced (warning).
  const existingForReport = reportContext
    ? invoices.filter((i) => i.report_id === reportContext.id).length
    : 0;
  // Rows shown: all, or only those of the ?report= filter.
  const visibleInvoices = reportFilter
    ? invoices.filter((i) => i.report_id === reportFilter.id)
    : invoices;

  if (loading) return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">{t('invoices.title')}</h1>
        <button className="btn btn-primary" onClick={openNewInvoice}>
          + {t('invoices.new')}
        </button>
      </div>

      {/* Editor */}
      {showEditor && (
        <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--color-primary)' }}>
          <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
            {reportContext
              ? `${t('invoices.invoiceFor')} : ${reportContext.title}`
              : t('invoices.new')}
          </h3>
          <form onSubmit={handleSave}>
            {reportContext && existingForReport > 0 && (
              <div style={{
                background: 'var(--color-warning-bg)', color: 'var(--color-warning-text)',
                border: '1px solid var(--color-accent)', borderRadius: 'var(--radius)',
                padding: '10px 14px', marginBottom: 16, fontSize: 13, fontWeight: 500,
              }}>
                ⚠ {t('invoices.alreadyInvoiced', { count: existingForReport })}
              </div>
            )}
            {/* Bill-to */}
            {reportContext ? (
              <div className="form-group">
                <label>{t('invoices.billTo')}</label>
                <input className="form-control" value={reportContext.billToName} disabled />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('invoices.billTo')}</label>
                  <select
                    className="form-control"
                    value={billTo}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBillTo(v);
                      let nm = '';
                      if (v.startsWith('company:')) nm = (customers.find((c) => `company:${c.id}` === v) || {}).name || '';
                      if (nm) setPoNumber(poDefault(nm));
                    }}
                  >
                    <option value="">{t('invoices.chooseParty')}</option>
                    {customers.map((c) => (
                      <option key={c.id} value={`company:${c.id}`}>{c.name}</option>
                    ))}
                    <option value="manual">{t('invoices.manualParty')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('invoices.currency')}</label>
                  <input
                    className="form-control"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
                    style={{ width: 120 }}
                  />
                </div>
              </div>
            )}

            {recipient && (
              <p className={recipient.email ? 'invoice-recipient' : 'invoice-recipient invoice-recipient-warn'}>
                {recipient.kind === 'company' ? t('invoices.billedCompany') : t('invoices.billedCustomer')}
                {' : '}
                <strong>{recipient.name}</strong>
                {recipient.email
                  ? ` — ${recipient.email}`
                  : ` — ⚠ ${t('invoices.noRecipientEmail')}`}
              </p>
            )}

            {!reportContext && billTo === 'manual' && (
              <div className="inline-form">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>{t('invoices.partyName')} *</label>
                    <input className="form-control" value={manual.name}
                      onChange={(e) => setManual({ ...manual, name: e.target.value })}
                      onBlur={() => { if (!poNumber && manual.name.trim()) setPoNumber(poDefault(manual.name)); }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>{t('invoices.partyVat')}</label>
                    <input className="form-control" value={manual.vat}
                      onChange={(e) => setManual({ ...manual, vat: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>{t('invoices.partyEmail')}</label>
                    <input className="form-control" value={manual.email}
                      onChange={(e) => setManual({ ...manual, email: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label>{t('invoices.partyPhone')}</label>
                    <input className="form-control" value={manual.phone}
                      onChange={(e) => setManual({ ...manual, phone: e.target.value })} />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label>{t('invoices.partyAddress')}</label>
                  <input className="form-control" value={manual.address}
                    onChange={(e) => setManual({ ...manual, address: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('invoices.partyPostalCode')}</label>
                    <input className="form-control" value={manual.postal_code}
                      onChange={(e) => setManual({ ...manual, postal_code: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('invoices.partyCity')}</label>
                    <input className="form-control" value={manual.city}
                      onChange={(e) => setManual({ ...manual, city: e.target.value })} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>{t('invoices.partyCountry')}</label>
                    <select className="form-control" value={manual.country}
                      onChange={(e) => setManual({ ...manual, country: e.target.value })}>
                      {MANUAL_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Purchase order (PEPPOL) — prefilled, editable */}
            <div style={{ display: 'grid', gridTemplateColumns: reportContext ? '1fr 1fr' : '1fr', gap: 16 }}>
              <div className="form-group">
                <label>{t('invoices.poNumber')}</label>
                <input className="form-control" value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder={t('invoices.poNumberPlaceholder')} />
              </div>
              {reportContext && (
                <div className="form-group">
                  <label>{t('invoices.currency')}</label>
                  <input className="form-control" value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))} />
                </div>
              )}
            </div>

            {/* Line items */}
            <div style={{ marginTop: 12, marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
              {t('invoices.lineItems')}
            </div>
            <div className="line-items">
              <div className="line-head">
                <span>{t('invoices.designation')}</span>
                <span>{t('invoices.qty')}</span>
                <span>{t('invoices.unitPrice')}</span>
                <span>{t('invoices.vat')}</span>
                <span>{t('invoices.lineTotal')}</span>
                <span />
              </div>
              {lines.map((l, idx) => (
                <div className="line-item" key={idx}>
                  <div className="li-field li-desig">
                    <label>{t('invoices.designation')}</label>
                    <input className="form-control" value={l.designation}
                      onChange={(e) => updateLine(idx, 'designation', e.target.value)}
                      placeholder={t('invoices.designationPlaceholder')} />
                  </div>
                  <div className="li-field li-qty">
                    <label>{t('invoices.qty')}</label>
                    <input className="form-control" type="number" min="0" step="0.5" value={l.quantity}
                      onChange={(e) => updateLine(idx, 'quantity', e.target.value)} />
                  </div>
                  <div className="li-field li-price">
                    <label>{t('invoices.unitPrice')}</label>
                    <input className="form-control" type="number" min="0" step="0.01" value={l.unit_price}
                      onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} />
                  </div>
                  <div className="li-field li-vat">
                    <label>{t('invoices.vat')}</label>
                    <select className="form-control" value={String(l.tax_rate)}
                      onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)}>
                      {!VAT_RATES.includes(String(l.tax_rate)) && (
                        <option value={String(l.tax_rate)}>{l.tax_rate} %</option>
                      )}
                      <option value="21">{t('invoices.vat21')}</option>
                      <option value="6">{t('invoices.vat6')}</option>
                    </select>
                  </div>
                  <div className="li-field li-total">
                    <label>{t('invoices.lineTotal')}</label>
                    <div className="li-total-val">{money(Number(l.quantity || 0) * Number(l.unit_price || 0))}</div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm li-remove"
                    onClick={() => removeLine(idx)} title={t('invoices.removeLine')}>✕</button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLines((p) => [...p, emptyLine()])}>
                + {t('invoices.addLine')}
              </button>
              {presets.length > 0 && (
                <select className="form-control" style={{ width: 240 }} value={presetToAdd}
                  onChange={(e) => { setPresetToAdd(e.target.value); if (e.target.value) addPresetLine(e.target.value); }}>
                  <option value="">{t('invoices.addFromPreset')}</option>
                  {presets.map((p) => (
                    <option key={p.id} value={p.id}>{p.label} — {money(p.default_price)}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <table style={{ fontSize: 14 }}>
                <tbody>
                  <tr><td style={{ padding: '2px 16px', color: 'var(--color-text-light)' }}>{t('invoices.subtotal')}</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>{money(totals.subtotal)}</td></tr>
                  <tr><td style={{ padding: '2px 16px', color: 'var(--color-text-light)' }}>{t('invoices.taxTotal')}</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>{money(totals.tax)}</td></tr>
                  <tr><td style={{ padding: '4px 16px', fontWeight: 700, color: 'var(--color-primary)' }}>{t('invoices.total')}</td>
                    <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 800, color: 'var(--color-primary)' }}>{money(totals.total)}</td></tr>
                  <tr><td style={{ padding: '2px 16px', color: 'var(--color-text-light)' }}>{t('invoices.amountPaid')}</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>{money(paidNum)}</td></tr>
                  <tr><td style={{ padding: '2px 16px', color: 'var(--color-text-light)' }}>{t('invoices.amountDue')}</td>
                    <td style={{
                      padding: '2px 0', textAlign: 'right', fontWeight: 700,
                      color: dueNum <= 0 ? 'var(--color-success)' : 'var(--color-text)',
                    }}>{money(dueNum)}</td></tr>
                </tbody>
              </table>
            </div>

            {/* Already-settled amount: recorded locally and mirrored to
                InvoiceNinja as a payment once the invoice is synced. */}
            <div className="form-group invoice-paid-row">
              <label htmlFor="invoice-amount-paid">{t('invoices.amountPaid')}</label>
              <div className="invoice-paid-controls">
                <input
                  id="invoice-amount-paid"
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  max={totals.total || undefined}
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                />
                <label className="invoice-paid-check">
                  <input
                    type="checkbox"
                    checked={totals.total > 0 && paidNum >= totals.total}
                    onChange={(e) => setAmountPaid(e.target.checked ? String(totals.total.toFixed(2)) : '0')}
                  />
                  {t('invoices.markFullyPaid')}
                </label>
              </div>
              <small style={{ color: 'var(--color-text-light)' }}>{t('invoices.amountPaidHint')}</small>
            </div>

            <div className="form-group">
              <label>{t('invoices.notes')}</label>
              <textarea className="form-control" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)} placeholder={t('invoices.notesPlaceholder')} />
            </div>

            {error && <p style={{ color: 'var(--color-danger)', fontSize: 13, fontWeight: 500 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? t('auth.loading') : t('invoices.createInvoice')}
              </button>
              <button className="btn btn-secondary" type="button" onClick={resetEditor}>{t('wizard.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Invoice list */}
      {reportFilter && (
        <div className="card" style={{
          marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
          flexWrap: 'wrap', padding: '12px 16px', borderLeft: '4px solid var(--color-primary)',
        }}>
          <span style={{ fontSize: 14 }}>
            {t('invoices.filteredByReport')} <strong>{reportFilter.title}</strong>
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            searchParams.delete('report');
            setSearchParams(searchParams, { replace: true });
          }}>
            {t('invoices.clearFilter')}
          </button>
        </div>
      )}
      {invoices.some((i) => i.e_invoice_status === 'failed') && (
        <div className="ai-error" role="alert" style={{ marginBottom: 16 }}>
          {t('invoices.einvFailedBanner', { count: invoices.filter((i) => i.e_invoice_status === 'failed').length })}
        </div>
      )}
      {visibleInvoices.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">&#128179;</div>
          <p>{reportFilter ? t('invoices.emptyForReport') : t('invoices.empty')}</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="invoice-list-head">
            <span>{t('invoices.number')}</span>
            <span>{t('invoices.client')}</span>
            <span>{t('invoices.amount')}</span>
            <span>{t('invoices.status')}</span>
            <span>{t('invoices.sync')}</span>
            <span>{t('admin.actions')}</span>
          </div>
          {visibleInvoices.map((inv) => (
            <div className="invoice-row" key={inv.id}>
              <div className="il-cell il-num" data-label={t('invoices.number')}>
                {inv.provider_invoice_number || <span style={{ color: 'var(--color-text-lighter)' }}>—</span>}
              </div>
              <div className="il-cell il-client">{inv.bill_to_name}</div>
              <div className="il-cell il-amount" data-label={t('invoices.amount')}>
                {fmtMoney(inv.total, inv.currency)}
              </div>
              <div className="il-cell il-status" data-label={t('invoices.status')}>
                <Badge color={BIZ_COLORS[inv.business_status]}>{t(`invoices.biz_${inv.business_status}`)}</Badge>
                <Badge
                  color={inv.is_paid ? '#16a34a' : '#d9534f'}
                  title={
                    inv.is_paid
                      ? undefined
                      : `${t('invoices.amountDue')} : ${fmtMoney(inv.amount_due, inv.currency)}`
                  }
                >
                  {inv.is_paid ? t('invoices.paidYes') : t('invoices.paidNo')}
                </Badge>
              </div>
              <div className="il-cell il-sync" data-label={t('invoices.sync')}>
                <Badge
                  color={SYNC_COLORS[inv.sync_status]}
                  title={inv.sync_status === 'error' ? inv.last_sync_error : undefined}
                >
                  {t(`invoices.sync_${inv.sync_status}`)}
                </Badge>
                {inv.needs_attention && inv.last_sync_error && (
                  <div className="il-error">{inv.last_sync_error.slice(0, 120)}</div>
                )}
                {inv.e_invoice_status && (
                  <Badge color={E_INVOICE_COLORS[inv.e_invoice_status]} title={inv.e_invoice_message || undefined}>
                    {t(`invoices.einv_${inv.e_invoice_status}`)}
                  </Badge>
                )}
                {inv.e_invoice_status === 'failed' && inv.e_invoice_message && (
                  <div className="il-error">{inv.e_invoice_message.slice(0, 200)}</div>
                )}
              </div>
              <div className="il-cell il-actions">
                {inv.sync_status === 'error' && (
                  <button className="btn btn-accent btn-sm" disabled={busyId === inv.id}
                    onClick={() => runAction(inv.id, retryInvoice)}>
                    {t('invoices.retry')}
                  </button>
                )}
                {inv.sync_status === 'synced' && !inv.emailed_at && (
                  <button className="btn btn-primary btn-sm" disabled={busyId === inv.id}
                    onClick={() => runAction(inv.id, sendInvoice)}>
                    {t('invoices.send')}
                  </button>
                )}
                {!inv.is_paid && (
                  <button
                    className="btn btn-accent btn-sm"
                    disabled={busyId === inv.id}
                    title={t('invoices.markPaidHint', { amount: fmtMoney(inv.amount_due, inv.currency) })}
                    onClick={() => handleMarkPaid(inv)}
                  >
                    {t('invoices.markPaid')}
                  </button>
                )}
                {inv.sync_status === 'synced' && (
                  <button className="btn btn-secondary btn-sm" disabled={busyId === inv.id}
                    onClick={() => runAction(inv.id, refreshInvoice)}>
                    {t('invoices.refresh')}
                  </button>
                )}
                {inv.provider_invoice_url && (
                  <a className="btn btn-ghost btn-sm" href={inv.provider_invoice_url}
                    target="_blank" rel="noopener noreferrer">
                    {t('invoices.viewInIN')}
                  </a>
                )}
                {!inv.provider_invoice_id && (
                  <button className="btn btn-ghost btn-sm" disabled={busyId === inv.id}
                    onClick={() => handleDelete(inv.id)}>
                    {t('invoices.delete')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
